<template>
  <div class="group flex space-x-4 items-start grow">
    <div class="flex space-x-4 grow">
      <div class="w-1/2">
        <div class="block">
          <select class="criterion" aria-label="Select searcher criterion" :name="myFieldIdFieldName" v-model="mySelectedFieldValue" @change="myCriterionChangeHandler">
            <option v-for="option in criteria" :key="option.value" :value="option.value">
              {{ option.text }}
            </option>
          </select>
        </div>

        <div class="block" v-show="mapChecked">
          <!-- This is for the criterion text -->
          <input v-if="myShowCriterionText" type="text" :name="myCriterionTextFieldName" aria-label="Searcher criterion text">

          <!-- This is for multiselect with static options -->
          <Multiselect v-else-if="myShowCriterionValueIds" :id="myParameterizedCriterionValueIdsFieldName" :name="myCriterionValueIdsFieldName" :options="myStaticOptionsForCriterion" aria-label="Search criterion values"></Multiselect>

          <!-- This is for multiselect with dynamic options -->
          <Multiselect v-else-if="!otherPrioritizeTag" :id="myParameterizedCriterionValueIdsFieldName" :name="myListValueIdsFieldName" :options="myDynamicOptionsForCriterion" aria-label="Search criterion values"></Multiselect>
        </div>
      </div>

      <div class="w-1/2">
        <div class="block">
          <select class="criterion" aria-label="Select searcher criterion" :name="otherFieldIdFieldName" v-model="otherSelectedFieldValue" @change="otherCriterionChangeHandler">
            <option v-for="option in criteria" :key="option.value" :value="option.value">
              {{ option.text }}
            </option>
          </select>
        </div>

        <div class="block" v-show="mapChecked">
          <!-- This is for the criterion text -->
          <input v-if="otherShowCriterionText" type="text" :name="otherCriterionTextFieldName" aria-label="Searcher criterion text">

          <!-- This is for multiselect with static options -->
          <Multiselect v-else-if="otherShowCriterionValueIds" :id="otherParameterizedCriterionValueIdsFieldName" :name="otherCriterionValueIdsFieldName" :options="otherStaticOptionsForCriterion" aria-label="Search criterion values"></Multiselect>

          <!-- This is for multiselect with dynamic options -->
          <Multiselect v-else :id="otherParameterizedCriterionValueIdsFieldName" :name="otherListValueIdsFieldName" :options="otherDynamicOptionsForCriterion" aria-label="Search criterion values"></Multiselect>
        </div>
      </div>
    </div>

    <div class="w-48"><!-- Importance -->
      <select aria-label="Select criterion importance" :name="importanceFieldName" v-model="selectedImportance" :disabled="disableImportance">
        <option v-for="importance in importances" :key="importance.value" :value="importance.value" :selected="importance.value==selectedImportance">
          {{ importance.text }}
        </option>
      </select>
    </div>

    <div class="w-16 py-1.5"><!-- Mapped -->
      <input type="checkbox" :name="mappedFieldName" v-model="checkedMapped" :disabled="disableMapped" />
    </div>

    <div class="w-16 py-1.5"><!-- Fuzzy -->
      <input type="checkbox" :name="fuzzyFieldName" v-model="checkedFuzzy" />
    </div>
  </div>
</template>

<script setup>
  import { ref, computed, onMounted } from 'vue'
  import Multiselect from "../components/Multiselect.vue"

  // CONSTANTS
  const KEYWORD = -1
  const GROUPS = -2
  const PRIORITIZE_TAG = -6
  const PREFERRED = 13
  const GLOBAL = -999
  // Text fields are TEXT_FIELD, TEXT_AREA, DATE, or DATE_TIME
  const TEXT_FIELDS = [2, 6, 8, 9]

  const props = defineProps({
    importances:                      { type: Array, required: true },
    myFieldIdFieldName:               { type: String, required: true },
    myCriterionTextFieldName:         { type: String, required: true },
    myListValueIdsFieldName:          { type: String, required: true },
    myCriterionValueIdsFieldName:     { type: String, required: true }, /* these are the names of the fields */
    otherFieldIdFieldName:            { type: String, required: true },
    otherCriterionTextFieldName:      { type: String, required: true },
    otherListValueIdsFieldName:       { type: String, required: true },
    otherCriterionValueIdsFieldName:  { type: String, required: true }, /* these are the names of the fields */
    importanceFieldName:              { type: String, required: true },
    mappedFieldName:                  { type: String, required: true },
    fuzzyFieldName:                   { type: String, required: true },
    criteria:                         { type: Object, required: true }, /* each hash in criteria needs to have { name, value, and type } */
    searchableGroups:                 { type: Object, required: true },
    searchableTags:                   { type: Object, required: true },
    programId:                        { type: Number, default: GLOBAL },
    initialValues:                    { type: Object, default: () => ({
        my_field_id: KEYWORD,
        my_criterion_text: '',
        my_criterion_value_ids: [],
        my_list_value_ids: [],
        other_field_id: KEYWORD,
        other_criterion_text: '',
        other_criterion_value_ids: [],
        other_list_value_ids: [],
        importance: PREFERRED,
        fuzzy: false,
        mapped: false
      })}
  })

  function parameterize(string, separator = '-') {
    return string
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '') // Remove non-word chars (except spaces and hyphens)
      .replace(/[\s_-]+/g, separator) // Replace spaces, underscores, and hyphens with separator
      .replace(new RegExp(`^${separator}+|${separator}+$`, 'g'), ''); // Remove leading/trailing separator
  }

  const selectedImportance = ref(props.initialValues.importance)
  const checkedFuzzy = ref(props.initialValues.fuzzy)
  const checkedMapped = ref(props.initialValues.mapped)
  const criteria = props.criteria
  const importances = props.importances
  const searchableGroups = props.searchableGroups
  const searchableTags = props.searchableTags
  const programId = props.programId
  const mySelectedFieldValue = ref(props.initialValues.my_field_id)
  const otherSelectedFieldValue = ref(props.initialValues.other_field_id)
  const myParameterizedCriterionValueIdsFieldName = parameterize(props.myCriterionValueIdsFieldName)
  const otherParameterizedCriterionValueIdsFieldName = parameterize(props.otherCriterionValueIdsFieldName)
  const myDynamicOptionsForCriterion = ref([])
  const otherDynamicOptionsForCriterion = ref([])

  // Computed (reactive) functions
  const otherPrioritizeTag = computed(() => {
    return (otherSelectedFieldValue.value == PRIORITIZE_TAG)
  })

  const disableImportance = computed(() => {
    return (mySelectedFieldValue.value == GROUPS)
  })

  const disableMapped = computed(() => {
    return (otherPrioritizeTag.value)
  })

  const mapChecked = computed(() => {
    return (otherPrioritizeTag.value || checkedMapped.value)
  })

  const myShowCriterionText = computed(() => {
    return (mySelectedFieldValue.value == KEYWORD || TEXT_FIELDS.includes(fieldType(mySelectedFieldValue.value)));
  })

  const myShowCriterionValueIds = computed(() => {
    // There is only "other_prioritize_tag?" in AlgorithmSearchCriterion
    // If it's selected, we don't show criterion value ids dropdown
    return (mySelectedFieldValue.value == GROUPS && otherSelectedFieldValue.value != PRIORITIZE_TAG)
  })

  const otherShowCriterionText = computed(() => {
    return (otherSelectedFieldValue.value == KEYWORD || TEXT_FIELDS.includes(fieldType(otherSelectedFieldValue.value)));
  })

  const otherShowCriterionValueIds = computed(() => {
    return (otherSelectedFieldValue.value == GROUPS || otherSelectedFieldValue.value == PRIORITIZE_TAG)
  })

  const myStaticOptionsForCriterion = computed(() => {
    switch (mySelectedFieldValue.value) {
    case GROUPS:
      return searchableGroups;
    default:
      return [];
    }
  })

  const otherStaticOptionsForCriterion = computed(() => {
    switch (otherSelectedFieldValue.value) {
    case GROUPS:
      return searchableGroups;
    case PRIORITIZE_TAG:
      return searchableTags;
    default:
      return [];
    }
  })

  // Regular functions
  const fieldType = (fieldId) => {
    return (criteria.filter((c) => c.value == fieldId)[0]?.type)
  }

  const myCriterionChangeHandler = () => {
    // Update the multiselect options here
    let params = null;

    if (programId == GLOBAL) {
      params = new URLSearchParams({ id: mySelectedFieldValue.value })
    } else {
      params = new URLSearchParams({ id: mySelectedFieldValue.value, program_id: programId })
    }
    // Call the backend to get the options associated with this criterion

    fetch(`/search/criterion?${params.toString()}`)
      .then(response => response.json())
      .then(json => myDynamicOptionsForCriterion.value = json)

    // Your complex field-specific logic here
    console.log('My criterion changed:', mySelectedFieldValue.value)
  }

  const otherCriterionChangeHandler = () => {
    // Update the multiselect options here
    let params = null;

    if (programId == GLOBAL) {
      params = new URLSearchParams({ id: otherSelectedFieldValue.value })
    } else {
      params = new URLSearchParams({ id: otherSelectedFieldValue.value, program_id: programId })
    }
    // Call the backend to get the options associated with this criterion

    fetch(`/search/criterion?${params.toString()}`)
      .then(response => response.json())
      .then(json => otherDynamicOptionsForCriterion.value = json)

    // Your complex field-specific logic here
    console.log('Other criterion changed:', otherSelectedFieldValue.value)
  }

  onMounted(() => {
    myCriterionChangeHandler();
    otherCriterionChangeHandler();
  })
</script>
